import React, { useState, useRef } from 'react';
import { apiImportCSV, apiExportLeads } from '../services/api';
import toast from 'react-hot-toast';

export default function ImportPage() {
    const [file, setFile] = useState(null);
    const [dragover, setDragover] = useState(false);
    const [importing, setImporting] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [result, setResult] = useState(null);
    const inputRef = useRef();

    const handleFile = (f) => {
        if (!f) return;
        if (!f.name.endsWith('.csv')) { toast.error('Please upload a .csv file'); return; }
        setFile(f);
        setResult(null);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragover(false);
        handleFile(e.dataTransfer.files[0]);
    };

    const handleImport = async () => {
        if (!file) return toast.error('Please select a CSV file first');
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await apiImportCSV(fd);
            const resultData = { ...res.data, total: (res.data.imported || 0) + (res.data.skipped || 0) };
            setResult(resultData);
            toast.success(`Import complete! ${res.data.imported} leads imported.`);
            setFile(null);
        } catch (e) {
            toast.error(e.response?.data?.message || 'Import failed');
        } finally {
            setImporting(false);
        }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const res = await apiExportLeads();
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `leads_export_${new Date().toISOString().split('T')[0]}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success('Export downloaded successfully!');
        } catch {
            toast.error('Export failed');
        } finally {
            setExporting(false);
        }
    };

    const csvTemplate = [
        'name,phone,email,course,source,status,notes',
        'Ravi Kumar,9876543210,ravi@email.com,Full Stack Web Development,Website,New,Interested in evening batch',
        'Priya Sharma,9123456789,priya@gmail.com,Data Science & ML,Facebook Ads,Contacted,',
    ].join('\n');

    const downloadTemplate = () => {
        const url = window.URL.createObjectURL(new Blob([csvTemplate], { type: 'text/csv' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = 'leads_import_template.csv';
        a.click();
        window.URL.revokeObjectURL(url);
    };

    return (
        <div className="page">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Import / Export</h1>
                    <p className="page-subtitle">Bulk import leads from CSV or export all leads</p>
                </div>
            </div>

            <div className="dashboard-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>

                {/* Import Card */}
                <div className="card">
                    <div className="card-header">
                        <span className="card-title">📤 Import Leads from CSV</span>
                        <button id="download-template-btn" className="btn btn-ghost btn-sm" onClick={downloadTemplate}>
                            ⬇ Template
                        </button>
                    </div>
                    <div className="card-body">
                        {/* Drop zone */}
                        <div
                            id="csv-drop-zone"
                            className={`upload-zone${dragover ? ' dragover' : ''}`}
                            onDragOver={(e) => { e.preventDefault(); setDragover(true); }}
                            onDragLeave={() => setDragover(false)}
                            onDrop={handleDrop}
                            onClick={() => inputRef.current?.click()}
                        >
                            <input
                                type="file"
                                accept=".csv"
                                ref={inputRef}
                                style={{ display: 'none' }}
                                id="csv-file-input"
                                onChange={(e) => handleFile(e.target.files[0])}
                            />
                            <div className="upload-zone-icon">📂</div>
                            {file ? (
                                <>
                                    <p className="primary">✅ {file.name}</p>
                                    <p className="secondary">{(file.size / 1024).toFixed(1)} KB — Click to change</p>
                                </>
                            ) : (
                                <>
                                    <p className="primary">Drop your CSV file here</p>
                                    <p className="secondary">or click to browse · Max 5 MB</p>
                                </>
                            )}
                        </div>

                        {/* Import button */}
                        <button
                            id="import-btn"
                            className="btn btn-primary btn-full"
                            style={{ marginTop: '16px' }}
                            onClick={handleImport}
                            disabled={!file || importing}
                        >
                            {importing
                                ? <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />Importing…</>
                                : '📤 Import CSV'}
                        </button>

                        {/* Result */}
                        {result && (
                            <div className="import-result" id="import-result">
                                <h3>Import Results</h3>
                                <div className="import-stat">
                                    <span>Total rows processed</span>
                                    <strong style={{ color: 'var(--text-primary)' }}>{result.total}</strong>
                                </div>
                                <div className="import-stat">
                                    <span>Successfully imported</span>
                                    <strong style={{ color: 'var(--success)' }}>{result.imported}</strong>
                                </div>
                                <div className="import-stat">
                                    <span>Skipped / Errors</span>
                                    <strong style={{ color: result.skipped ? 'var(--warning)' : 'var(--text-muted)' }}>{result.skipped || 0}</strong>
                                </div>
                                {result.errors?.length > 0 && (
                                    <div className="import-errors">
                                        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '6px' }}>Error details:</p>
                                        {result.errors.slice(0, 5).map((err, i) => (
                                            <div key={i} className="import-error-item">• {err}</div>
                                        ))}
                                        {result.errors.length > 5 && <div className="import-error-item">… and {result.errors.length - 5} more</div>}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Export Card */}
                <div className="card">
                    <div className="card-header">
                        <span className="card-title">📥 Export Leads to CSV</span>
                    </div>
                    <div className="card-body">
                        <div style={{ textAlign: 'center', padding: '32px 0' }}>
                            <div style={{ fontSize: '4rem', marginBottom: '16px' }}>📊</div>
                            <p style={{ color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: 500 }}>Download all leads as CSV</p>
                            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '28px' }}>
                                Exports all lead data including name, phone, email, course, source, status, counselor, and notes.
                            </p>
                            <button
                                id="export-csv-btn"
                                className="btn btn-success btn-lg"
                                onClick={handleExport}
                                disabled={exporting}
                            >
                                {exporting
                                    ? <><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Exporting…</>
                                    : '📥 Export All Leads'}
                            </button>
                        </div>

                        {/* Format info */}
                        <div style={{ background: 'var(--bg-base)', borderRadius: 'var(--radius)', padding: '16px', marginTop: '12px' }}>
                            <p style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Export Format</p>
                            <code style={{ fontSize: '0.75rem', color: 'var(--accent-light)', lineHeight: 1.7 }}>
                                Excel (.xlsx) — id, name, phone, email, course, source, status, counselor, notes, created_at
                            </code>
                        </div>
                    </div>
                </div>

                {/* Instructions Card */}
                <div className="card" style={{ gridColumn: '1 / -1' }}>
                    <div className="card-header"><span className="card-title">📋 Import Instructions</span></div>
                    <div className="card-body">
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
                            {[
                                { icon: '1️⃣', title: 'Download Template', desc: 'Click the "Template" button to download a sample CSV with the correct column format.' },
                                { icon: '2️⃣', title: 'Fill in Your Data', desc: 'Required fields: name, phone, course. Optional: email, source, status, notes.' },
                                { icon: '3️⃣', title: 'Valid Values', desc: 'Source: Website, Facebook Ads, Instagram Ads, Walk-in, Referral. Status: New, Contacted, Follow-up, Will Visit, Visited, Converted, Not Interested.' },
                                { icon: '4️⃣', title: 'Upload & Import', desc: 'Drop or browse to select your CSV. Duplicate phone numbers will be automatically skipped.' },
                            ].map(step => (
                                <div key={step.title} style={{ display: 'flex', gap: '14px' }}>
                                    <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>{step.icon}</span>
                                    <div>
                                        <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px', fontSize: '0.875rem' }}>{step.title}</div>
                                        <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>{step.desc}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
